<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="mobile-login-landing"
       :class="{
         'mobile-login-landing--compact': !hasVisibleCobranding,
         'mobile-login-landing--verify': hasSentVerificationCode
       }">
    <GdprCookieBanner v-if="isGdprBannerVisible"
                      @cookie-accept="acceptCookieConsent" />
    <div class="mobile-login__container">
      <div v-if="!hasSentVerificationCode"
           class="mobile-login__header">
        <div class="mobile-login__logo-header">
          <img v-if="!hasSentVerificationCode"
               :src="expertAiLogo"
               class="mobile-login__expert-ai-logo"
               alt="Expert AI">
        </div>
        <div class="mobile-login__description">

          <div v-if="isGuestPassUser" class="mobile-login__description-guest-pass">
            <p class="ds1-mt-0">
              Please enter the information below to activate your free UpToDate trial.
            </p>
            <ul>
              <li v-for="(item, index) in UTD_GUEST_PASS_REGISTRATION_MESSAGES"
                  :key="index">
                <p>{{ item }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <IapRegistrationForm @cancel-registration="cancelRegistration"
                           @update:has-sent-verification-code="hasSentVerificationCode = $event"
                           @update:user-email="userEmail = $event" />
    </div>
    <BcdrNoticeModal ref="registerBcdrModal" feature-name="UpToDate Registration" />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, onBeforeUnmount, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { SET_SHOULD_HIDE_HEADER_ITEMS } from '_acaSrc/store/feature.store';
import { SET_IS_XAI_MOBILE } from '_acaSrc/store/device.store';
import IapRegistrationForm from './IapRegistrationForm.vue';
import {
    UTD_GUEST_PASS_REGISTRATION_MESSAGES,
    GUEST_PASS_LICENSE,
    C_HTTP
} from '_acaSrc/utility/constants';
import GdprCookieBanner from '_acaSrc/components/header/GdprCookieBanner.vue';
import BcdrNoticeModal from '_acaSrc/components/error/BcdrNoticeModal.vue';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import { getCookie } from '_acaSrc/utility/Cookies';
import IndexManager from '_acaSrc/utility/IndexManager';
import UtdCache from '_acaSrc/utility/UtdCache';
import expertAiLogo from '~_acaAssets/search/expert-ai-logo.svg';
const store = useStore();


const registerBcdrModal = ref(null);
const hasSentVerificationCode = ref(false);
const userEmail = ref('');

const isDisasterRecovery = computed(() => store.getters['app/isDisasterRecovery']);

const shouldCloseGdprBanner = ref(false);
const acceptCookieConsent = () => {
    shouldCloseGdprBanner.value = true;
};
const isAcceptCookieConsent = computed(() => getCookie('cookie-accept') === 't');
const isGdprBannerVisible = computed(() =>
    store.getters['feature/isShowGdprBanner']
    && !isAcceptCookieConsent.value
    && !shouldCloseGdprBanner.value
);

const registrationInfo = computed(() => store.getters['login/registrationInfo']);
const coBrandingHeader = computed(() => registrationInfo.value.coBrandingHeader);
const isGuestPassUser = computed(() =>
    store.getters['footer/licensee'] === GUEST_PASS_LICENSE
    || store.getters['profile/userProfileVisitor'].isGuestUser);
const hasVisibleCobranding = computed(() => isGuestPassUser.value);

const router = computed(() => store.getters['app/router']);
const cancelRegistration = () => {
    router.value.url(C_HTTP.DEFAULT_HOMEPAGE_PATH);
};

const { prefillFormFields, form } = useRegistrationForm();
const countryOptions = computed(() => store.getters['account/myAccountCountries']);

const normalizeCountryToIndex = () => {
    if (typeof form.country !== 'string') {
        return;
    }
    const idx = countryOptions.value.findIndex(c => c.value === form.country);
    form.country = idx >= 0 ? idx : null;
};

const registrationForm = computed(() => store.getters['login/registrationForm']);
const registrationFormReady = computed(() => registrationForm.value.isLoaded);
const isAuthenticatedByIp = computed(() => store.getters['app/isAuthenticatedByIp']);

watch(registrationFormReady, (newValue) => {
    if (newValue) {
        prefillFormFields();
        normalizeCountryToIndex();
        if (isDisasterRecovery.value) {
            registerBcdrModal.value?.openModal();
        }
    }
}, { immediate: true });

onMounted(() => {
    store.commit(`feature/${SET_SHOULD_HIDE_HEADER_ITEMS}`, true);

    store.commit(`device/${SET_IS_XAI_MOBILE}`, true);
    new UtdCache().setPersistent('isXAIMobile', true, 1);
    new IndexManager().bodyCss.setOrClear('xAIMobile', true);
});

onBeforeUnmount(() => {
    new IndexManager().bodyCss.clear('xAIMobile');
    store.commit(`device/${SET_IS_XAI_MOBILE}`, false);
});

onUnmounted(() => {
    store.commit(`feature/${SET_SHOULD_HIDE_HEADER_ITEMS}`, false);
});
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

@cobranding-logo-width-desktop: 310px;
@cobranding-logo-width-mobile: 288px;
@cobranding-logo-height: 75px;

@media only screen and (min-width: 768px) {
  .mobile-login__cobranding {
    .mobile-login__cobranding-img {
      width: @cobranding-logo-width-desktop;
    }
    .mobile-login__cobranding-img-content {
      max-width: @cobranding-logo-width-desktop;
    }
  }
  .mobile-login__description-cobranding-content {
    width: 75%;
  }
}

.mobile-login-landing {
  .ds1-ph-3();
  .ds1-pv-5();
  .ds1-flex-horizontal-center();
  flex-direction: column;
  align-items: center;
  font-size: 14px;

  &--verify {
    background-color: @WKCE-WHITE;
    justify-content: flex-start !important;
    min-height: 100vh;
    padding: 0;
    align-items: center;

    .mobile-login__container {
      display: block;
      width: 100%;
      margin: 0 auto;
      padding-top: 0;
      justify-content: flex-start !important;
    }
  }
}

.mobile-login__header {
  .ds1-ma-0();
  width: 100%;
}

.mobile-login__container {
  .ds1-flex-horizontal-center();
  max-width: 690px;
  flex-direction: column;
  align-items: center;
  background-color: @WKCE-WHITE;
  width: 100%;
}

.mobile-login__logo-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.mobile-login__expert-ai-logo {
  .ds1-pt-4();
  .ds1-ph-4();
  .ds1-pb-2();
  width: 100%;
  object-fit: contain;
  box-sizing: border-box;
}

.mobile-login__verify-email-heading {
  .ds1-pt-4();
  .ds1-ph-4();
  .ds1-pb-2();
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  color: @WKCE-GRAY-SHADE2;
}

.mobile-login__verify-email-subtext {
  .ds1-ph-4();
  margin: 0;
  font-size: 14px;
  text-align: center;
  color: @WKCE-GRAY-SHADE2;
}

.mobile-login__cobranding {
  .ds1-flex-vertical-center();
  background-color: transparent;
  flex-direction: column;
  width: 100%;
  height: @cobranding-logo-height;
}

.mobile-login__cobranding-img {
  .ds1-flex-vertical-center();
  background-color: transparent;
  flex-direction: column;
  width: @cobranding-logo-width-mobile;
  height: @cobranding-logo-height;
}

.mobile-login__cobranding-img-content {
  object-fit: cover;
  align-items: center;
  max-width: @cobranding-logo-width-mobile;
  max-height: @cobranding-logo-height;
}

.mobile-login__description {
  .ds1-flex-horizontal-center();
  flex-direction: column;
  align-items: center;
}

.mobile-login__description-cobranding {
  .ds1-flex-horizontal-center();
}

.mobile-login__description-cobranding-content {
  line-height: 1.75;
}

.mobile-login-landing--compact {
  .mobile-login__header {
    align-self: flex-start;
  }
}

.mobile-login__description-iap {
  p {
    margin-bottom: 12px;
    line-height: 1.6;
    font-size: 14px;
  }
}

.mobile-login__iap-link {
  color: @DS1-UTD-PRIMARY-LINK-BASE-COLOR;
  text-decoration: underline;

  &:hover {
    color: @WKCE-BLUE-SHADE1;
  }
}

.xAIMobile {
  height: 100vh;

  .mobile-login-landing {
    .ds1-ph-0();
    .ds1-pv-0();
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    background-color: @WKCE-WHITE;

    &--verify {
      background-color: @WKCE-WHITE;
      justify-content: flex-start !important;

      .mobile-login__container {
        background-color: @WKCE-WHITE;
        border-radius: 0;
        display: block;
        width: 100%;
        margin: 0 auto;
        padding-top: 0;
        overflow-y: unset;
      }
    }
  }

  .mobile-login__xai-logo-header {
    background: @WKCE-WHITE;
    display: flex;
    height: 133px;
    padding: 8px 0;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    width: 100%;
    flex: 1;
  }

  .mobile-login__xai-logo {
    .ds1-pa-4();
    width: 100%;
    height: 100%;
    object-fit: contain;
    box-sizing: border-box;
  }

  .mobile-login__container {
    flex: 4;
    overflow-y: auto;
    max-width: none;
    border-radius: 16px 16px 0 0;
    background-color: @WKCE-CREAM-TINT2;
    padding: 0;
    align-items: stretch;
  }

  .mobile-login__header {
    .ds1-ph-3();
    .ds1-pt-3();
    width: 100%;
    box-sizing: border-box;
    align-self: stretch;
  }

  .mobile-login__title {
    color: @WKCE-CREAM-SHADE2;
    font-size: 22px;
    font-weight: 700;
    text-align: left;
    width: 100%;
  }

  .mobile-login__description {
    align-items: flex-start;
    width: 100%;
    box-sizing: border-box;
  }

  .mobile-login__description-iap {
    p {
      color: @WKCE-CREAM-SHADE2;
    }
  }

  .mobile-login__iap-link {
    color: @DS1-UTD-PRIMARY-LINK-BASE-COLOR;
    text-decoration: underline;

    &:hover {
      color: @WKCE-BLUE-SHADE1;
    }
  }

  .mobile-login__description-guest-pass {
    p, li {
      color: @WKCE-CREAM-SHADE2;
    }
  }

  .mobile-login__description-cobranding-content {
    p {
      color: @WKCE-CREAM-SHADE2;
    }
  }
}
</style>

